import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Detail-page cache fallback. When a `useThing(id)` hook fires offline and
 * the per-id query was never persisted, the fetch fails and the page is
 * blank. But we usually DO have the row in the list cache (the prewarm
 * pulled `useThings()` at login). This helper walks every variant of
 * `LIST_KEY` (`['articles']`, `['articles', undefined]`, `['articles',
 * filters]` …) and returns the first row whose id matches.
 *
 * Wire it into `useQuery({ placeholderData: () => listCacheFallback(...) })`.
 * The placeholder lets the detail page render the row instantly; the live
 * query then refines or fails silently.
 */
export function listCacheFallback<T extends { id: string }>(
  qc: QueryClient,
  listKey: QueryKey,
  id: string,
): T | undefined {
  const matches = qc.getQueriesData<T[] | undefined>({ queryKey: listKey });
  for (const [, list] of matches) {
    if (Array.isArray(list)) {
      const found = list.find((row) => row && row.id === id);
      if (found) return found;
    }
  }
  return undefined;
}
